// Thin REST/SSE client for peerception-api, cookie + bearer auth with refresh rotation (same
// scheme as the modelling add-in), and the /agent-v2 transport of PLAN-agent-v2 §10.
// Keep this class small: auth + transport only. Tool execution lives in ToolRunner, Office.js in
// ExcelToolExecutor.
import { readToken } from './tokenStore';
import { refreshAccessToken, setAuthCookies, logout as authLogout } from './refreshAuth';
import { parseSseStream } from './agent/sse';
import type { CircularBlock } from './excel/convergence';
import type { AccessInfo, ChatRequest, DocumentSummary, FeedbackReceipt, FeedbackRequest, ModelListing, ReportReceipt, ReportRequest, ResumeRequest, SkillListing, SSEEvent, SSEEventType, ThreadDetail, ThreadListing, ThreadSummary, UndoRecord, UsageSummary } from '../types/agent';
import type { AuditReport, CaptureChunkBody, CaptureUploadResponse } from '../types/audit';
import type { IndexJobStart, IndexJobStarted, IndexStatus } from '../types/index';
import type { FilledTemplate } from '../types/excel';
import { ACCESS_REQUIRED_DETAIL, RATE_LIMITED_DETAIL, TOO_MANY_REPORTS } from '../types/agent';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

/** GET /user (sculpt-api `UserOut`): the fields the pane reads. */
export interface CurrentUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  email_verified?: boolean;
  organization?: { id: number; name: string; role: string } | null;
  /** False until the question of the first connection is answered or skipped (components/Discovery). */
  discovery_asked?: boolean;
}

/** The answers to the question of the first connection (sculpt-api `users/schemas.py`), in the order shown. */
export const DISCOVERY_SOURCES = ['linkedin', 'google', 'youtube', 'word_of_mouth', 'other'] as const;
export type DiscoverySource = (typeof DISCOVERY_SOURCES)[number];

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string,
  ) {
    super(`API ${status}: ${body}`);
  }

  /** The `detail` of a FastAPI error body (a string code or an object), undefined when the body is not JSON. */
  get detail(): unknown {
    try {
      return (JSON.parse(this.body) as { detail?: unknown }).detail;
    } catch {
      return undefined;
    }
  }
}

/** A 429 `rate_limited` from a credential route: too many failed sign-in attempts. */
export function isRateLimited(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429 && err.detail === RATE_LIMITED_DETAIL;
}

/** A 429 `too_many_reports` from POST /agent-v2/reports: the user's reports of the last 24 hours reached the cap. */
export function isTooManyReports(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429 && err.detail === TOO_MANY_REPORTS;
}

export class ApiService {
  private host = BASE_URL;
  private accessDeniedListeners = new Set<() => void>();
  private unauthorizedListeners = new Set<() => void>();

  /** Called whenever a gated /agent-v2 call answers 403 `agent_v2_access_required` (the user lost
   *  or never had the entitlement). The UI switches to the no-access screen instead of surfacing
   *  the error in the chat. Returns an unsubscribe function. */
  onAccessDenied(listener: () => void): () => void {
    this.accessDeniedListeners.add(listener);
    return () => this.accessDeniedListeners.delete(listener);
  }

  /** Called whenever an authenticated call still answers 401 once the refresh has had its chance: the session is
   *  over, and the UI goes back to the login. Returns an unsubscribe function. */
  onUnauthorized(listener: () => void): () => void {
    this.unauthorizedListeners.add(listener);
    return () => this.unauthorizedListeners.delete(listener);
  }

  private notifyUnauthorized(): void {
    this.unauthorizedListeners.forEach((l) => l());
  }

  /** True (and listeners notified) when the response is the entitlement 403. */
  private async detectAccessDenied(res: Response): Promise<boolean> {
    if (res.status !== 403) return false;
    let detail = '';
    try {
      const body = (await res.clone().json()) as { detail?: unknown };
      detail = typeof body.detail === 'string' ? body.detail : '';
    } catch {
      return false;
    }
    if (detail !== ACCESS_REQUIRED_DETAIL) return false;
    this.accessDeniedListeners.forEach((l) => l());
    return true;
  }

  private authHeader(): Record<string, string> {
    const token = readToken('accessToken');
    const type = readToken('tokenType');
    return token && type ? { Authorization: `${type} ${token}` } : {};
  }

  /** Authenticated fetch with one transparent refresh-and-retry on 401; a 401 that survives it notifies
   *  `onUnauthorized`. */
  private async request(path: string, init: RequestInit = {}, _retried = false): Promise<Response> {
    const res = await fetch(`${this.host}${path}`, {
      ...init,
      headers: { accept: 'application/json', ...this.authHeader(), ...(init.headers || {}) },
    });
    if (res.status === 401 && !_retried) {
      const ok = await refreshAccessToken();
      if (ok) return this.request(path, init, true);
    }
    if (res.status === 401) this.notifyUnauthorized();
    await this.detectAccessDenied(res);
    return res;
  }

  private async json<T>(res: Response): Promise<T> {
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ''));
    return (await res.json()) as T;
  }

  private jsonInit(method: string, body?: unknown): RequestInit {
    return { method, headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) };
  }

  // ---- Auth ------------------------------------------------------------------
  async classicLogin(email: string, password: string): Promise<void> {
    const res = await fetch(`${this.host}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', accept: 'application/json' },
      body: new URLSearchParams({ username: email, password }),
    });
    const data = await this.json<{ access_token: string; token_type: string; refresh_token?: string }>(res);
    setAuthCookies(data);
  }

  /** Validate the current session (used on startup). */
  async getCurrentUser(): Promise<CurrentUser> {
    return this.json(await this.request('/user'));
  }

  /** PUT /account/discovery — the question of the first connection, answered or skipped (`source` null). */
  async answerDiscovery(source: DiscoverySource | null): Promise<CurrentUser> {
    return this.json(await this.request('/account/discovery', this.jsonInit('PUT', { source })));
  }

  /** POST /token/portal-link — a single-use code (2 minutes) the portal trades for its own session (services/portal.ts). */
  async portalLink(): Promise<{ code: string; expires_in: number }> {
    return this.json(await this.request('/token/portal-link', this.jsonInit('POST')));
  }

  /** POST /signup/resend — a fresh verification mail for an account whose address is not confirmed yet. */
  async resendVerification(): Promise<{ sent: boolean }> {
    return this.json(await this.request('/signup/resend', this.jsonInit('POST')));
  }

  async logout(): Promise<void> {
    await authLogout();
  }

  /** GET /agent-v2/access — entitlement + allowance of the signed-in user (not gated). */
  async getAccess(): Promise<AccessInfo> {
    return this.json<AccessInfo>(await this.request('/agent-v2/access'));
  }

  /** GET /agent-v2/formula-rules — the formula rules the server enforces (sculpt-api); agent_v2's backend has no
   *  such route and answers 404, which leaves the lint on its built-in rules. */
  async getFormulaRules(): Promise<unknown> {
    return this.json<unknown>(await this.request('/agent-v2/formula-rules'));
  }

  // ---- Agent v2: streaming ---------------------------------------------------------
  /** POST /agent-v2/chat → SSE events (§10.2). */
  chat(body: ChatRequest, signal?: AbortSignal): AsyncGenerator<SSEEvent> {
    return this.stream('/agent-v2/chat', body, signal);
  }

  /** POST /agent-v2/resume → SSE events; resumes the interrupted graph with the tool results. */
  resume(body: ResumeRequest, signal?: AbortSignal): AsyncGenerator<SSEEvent> {
    return this.stream('/agent-v2/resume', body, signal);
  }

  /** POST /agent-v2/threads/{id}/continue → SSE events; wakes a run cut short (server `stalled` event,
   *  closed pane) from its last checkpoint — no new input, the graph re-executes the step that never finished. */
  continueThread(threadId: string, signal?: AbortSignal): AsyncGenerator<SSEEvent> {
    return this.stream(`/agent-v2/threads/${encodeURIComponent(threadId)}/continue`, {}, signal);
  }

  /** POST /agent-v2/threads/{id}/cancel → resolves a pending interrupt with cancelled results. */
  async cancelThread(threadId: string): Promise<void> {
    const res = await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}/cancel`, this.jsonInit('POST', {}));
    if (!res.ok && res.status !== 404) throw new ApiError(res.status, await res.text().catch(() => ''));
  }

  private async *stream(path: string, body: unknown, signal?: AbortSignal, _retried = false): AsyncGenerator<SSEEvent> {
    const res = await fetch(`${this.host}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'text/event-stream', ...this.authHeader() },
      body: JSON.stringify(body),
      signal,
    });
    if (res.status === 401) {
      if (!_retried && (await refreshAccessToken())) {
        yield* this.stream(path, body, signal, true);
        return;
      }
      this.notifyUnauthorized();
      throw new ApiError(401, 'Session expired — please log in again.');
    }
    await this.detectAccessDenied(res);
    if (!res.ok || !res.body) throw new ApiError(res.status, await res.text().catch(() => 'Agent request failed'));
    for await (const { event, data } of parseSseStream(res.body)) {
      yield { event: event as SSEEventType, data };
    }
  }

  // ---- Agent v2: attached documents (2026-09-10) --------------------------------------
  /** POST /agent-v2/documents (multipart) → the extracted document's summary. The browser sets the
   *  multipart boundary itself, so no Content-Type header here. */
  async uploadDocument(file: File, threadId: string | null, signal?: AbortSignal): Promise<DocumentSummary> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (threadId) form.append('thread_id', threadId);
    const res = await this.request('/agent-v2/documents', { method: 'POST', body: form, signal });
    if (!res.ok) throw new ApiError(res.status, await this.errorDetail(res));
    return (await res.json()) as DocumentSummary;
  }

  /** GET /agent-v2/documents/{id} — polled while a document's OCR runs (`status: 'processing'`). */
  async getDocument(documentId: string): Promise<DocumentSummary> {
    return this.json<DocumentSummary>(await this.request(`/agent-v2/documents/${encodeURIComponent(documentId)}`));
  }

  /** DELETE /agent-v2/documents/{id} — only a document not sent yet (409 once it belongs to a thread). */
  async deleteDocument(documentId: string): Promise<void> {
    const res = await this.request(`/agent-v2/documents/${encodeURIComponent(documentId)}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw new ApiError(res.status, await this.errorDetail(res));
  }

  /** The `detail` of a FastAPI error body, or the raw text. */
  private async errorDetail(res: Response): Promise<string> {
    const text = await res.text().catch(() => '');
    try {
      const body = JSON.parse(text) as { detail?: unknown };
      if (typeof body.detail === 'string') return body.detail;
    } catch {
      /* not JSON */
    }
    return text;
  }

  // ---- Agent v2: threads / usage ----------------------------------------------------
  /** The open workbook's threads (writable), its ancestors' (read-only, grouped), and with `all` every thread of
   *  the user, each writable only in its own workbook (PLAN-workbook-identity.md §6). */
  async listThreads(workbookId: string | null, opts: { all?: boolean } = {}): Promise<ThreadListing> {
    const q = new URLSearchParams();
    if (workbookId) q.set('workbook_id', workbookId);
    if (opts.all) q.set('all', '1');
    const qs = q.toString();
    return this.json<ThreadListing>(await this.request(`/agent-v2/threads${qs ? `?${qs}` : ''}`));
  }

  /** `workbookId`: the file open now, which `writable` is answered for. */
  async getThread(threadId: string, workbookId?: string | null): Promise<ThreadDetail> {
    const q = workbookId ? `?workbook_id=${encodeURIComponent(workbookId)}` : '';
    return this.json<ThreadDetail>(await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}${q}`));
  }

  /** Continue a read-only thread in the open workbook: a new thread started from a summary of it (§7), on `model`
   *  (omitted: the server's default). */
  async resumeHere(threadId: string, workbookId: string, model?: string | null): Promise<ThreadSummary> {
    const res = await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}/resume-here`,
      this.jsonInit('POST', { workbook_id: workbookId, ...(model ? { model } : {}) }));
    if (!res.ok) throw new ApiError(res.status, await this.errorDetail(res));
    return (await res.json()) as ThreadSummary;
  }

  /** GET /agent-v2/models — the models the picker lists, each allowed or not by the user's organization. */
  async getModels(): Promise<ModelListing> {
    return this.json<ModelListing>(await this.request('/agent-v2/models'));
  }

  /** GET /agent-v2/skills — the Skills the composer's `/` menu lists. */
  async getSkills(): Promise<SkillListing> {
    return this.json<SkillListing>(await this.request('/agent-v2/skills'));
  }

  /** POST /agent-v2/templates/{template}/fill — the template filled with the answers, as a base64 .xlsx, for
   *  `excel_insert_model` (the file never travels through the model). Throws ApiError: 404 unknown template, 422
   *  `{detail: {problems: [...]}}`. */
  async fillTemplate(template: string, technology: string, inputs: Record<string, unknown>): Promise<FilledTemplate> {
    return this.json<FilledTemplate>(await this.request(`/agent-v2/templates/${encodeURIComponent(template)}/fill`, this.jsonInit('POST', { technology, inputs })));
  }

  async renameThread(threadId: string, title: string): Promise<void> {
    await this.json(await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}`, this.jsonInit('PATCH', { title })));
  }

  async deleteThread(threadId: string): Promise<void> {
    const res = await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}`, { method: 'DELETE' });
    if (!res.ok) throw new ApiError(res.status, await res.text().catch(() => ''));
  }

  // ---- Protected sheets, circular blocks, persisted undo ----------------------------------

  /** GET/PUT /agent-v2/workbooks/{id}/protected-sheets — the pane toggle of domain #36. */
  async getProtectedSheets(workbookId: string): Promise<string[]> {
    const r = await this.json<{ sheets: string[] }>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/protected-sheets`));
    return r.sheets ?? [];
  }

  async setProtectedSheets(workbookId: string, sheets: string[]): Promise<string[]> {
    const r = await this.json<{ sheets: string[] }>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/protected-sheets`, this.jsonInit('PUT', { sheets })));
    return r.sheets ?? [];
  }

  /** GET /agent-v2/workbooks/{id}/circular — circular modules of the map (domain #35). */
  async getCircularBlocks(workbookId: string): Promise<CircularBlock[]> {
    const r = await this.json<{ blocks: CircularBlock[] }>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/circular`));
    return r.blocks ?? [];
  }

  /** POST /agent-v2/undo — before-image of a write, persisted so undo survives a pane reload. */
  async saveUndo(record: Omit<UndoRecord, 'stale' | 'id' | 'created_at'> & { thread_id: string; workbook_id?: string | null }): Promise<void> {
    const res = await this.request('/agent-v2/undo', this.jsonInit('POST', record));
    if (!res.ok) throw new Error(`saveUndo failed: ${res.status}`);
  }

  /** GET /agent-v2/undo — restorable entries; pass live fingerprints so stale entries are flagged. */
  async listUndo(threadId: string, fingerprints?: Record<string, string | null>, lastN = 20, workbookId?: string | null): Promise<UndoRecord[]> {
    const q = new URLSearchParams({ thread_id: threadId, last_n: String(lastN) });
    if (fingerprints) q.set('fingerprints', JSON.stringify(fingerprints));
    // The file open now: an entry written in another workbook comes back with `other_workbook` (D13).
    if (workbookId) q.set('workbook_id', workbookId);
    return this.json<UndoRecord[]>(await this.request(`/agent-v2/undo?${q.toString()}`));
  }

  /** How long the agent took on a turn, once it has ended: the server gives it back with the thread's history. */
  async recordTurnTime(threadId: string, turnIndex: number, durationMs: number): Promise<void> {
    const res = await this.request(`/agent-v2/threads/${encodeURIComponent(threadId)}/turns/${turnIndex}/time`, this.jsonInit('POST', { duration_ms: Math.round(durationMs) }));
    if (!res.ok) throw new Error(`recordTurnTime failed: ${res.status}`);
  }

  async markUndone(threadId: string, journalId: string): Promise<void> {
    const res = await this.request(`/agent-v2/undo/${encodeURIComponent(journalId)}/undone`, this.jsonInit('POST', { thread_id: threadId }));
    if (!res.ok) throw new Error(`markUndone failed: ${res.status}`);
  }

  async getUsage(): Promise<UsageSummary> {
    return this.json<UsageSummary>(await this.request('/agent-v2/usage'));
  }

  // ---- Satisfaction feedback: capture only, no read-back in this pass ----------------------

  /** POST /agent-v2/feedback — idempotent upsert on (user, thread, turn_index). Re-rating updates the row
   *  in place, which is what lets the hover thumbs send the thumb first and the chips a moment later. */
  async sendFeedback(body: FeedbackRequest): Promise<FeedbackReceipt> {
    return this.json<FeedbackReceipt>(await this.request('/agent-v2/feedback', this.jsonInit('POST', body)));
  }

  /** POST /agent-v2/reports — the open conversation reported to the Sculpt team (staff are mailed, and resolve it in
   *  the backoffice). 429 `too_many_reports` past the daily cap. */
  async reportConversation(body: ReportRequest): Promise<ReportReceipt> {
    return this.json<ReportReceipt>(await this.request('/agent-v2/reports', this.jsonInit('POST', body)));
  }

  /** DELETE /agent-v2/feedback — un-rate (the user clicked the thumb that was already lit). */
  async clearFeedback(threadId: string, turnIndex?: number): Promise<void> {
    const q = new URLSearchParams({ thread_id: threadId });
    if (turnIndex != null) q.set('turn_index', String(turnIndex));
    const res = await this.request(`/agent-v2/feedback?${q.toString()}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 404) throw new ApiError(res.status, await res.text().catch(() => ''));
  }

  // ---- Audit (AUDIT_SPEC §2.4): capture channel and Audit sheet report ----------------------

  // ---- Workbook index (pane open, 2026-09-14) ----------------------------------------
  async startIndexJob(workbookId: string, body: IndexJobStart): Promise<IndexJobStarted> {
    return this.json<IndexJobStarted>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/index/jobs`, this.jsonInit('POST', body)));
  }

  /** The answer to the identity question (PLAN-workbook-identity.md §4.2): the id to write into the file and index
   *  under — the announced one for `same`, a new one the caller owns for `fork` / `other`. */
  async answerIdentity(workbookId: string, answer: 'same' | 'fork' | 'other', body: { document_url: string; name: string }): Promise<{ workbook_id: string }> {
    return this.json<{ workbook_id: string }>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/identity/${answer}`, this.jsonInit('POST', body)));
  }

  /** "Resume" on the identity question: carry on with the caller's workbook that already knows this path (§4.3). */
  async resumeIdentity(workbookId: string, body: { document_url: string; target: string }): Promise<{ workbook_id: string }> {
    return this.json<{ workbook_id: string }>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/identity/resume`, this.jsonInit('POST', body)));
  }

  /** PUT one whole sheet of the index job; gzipped JSON when the WebView can compress. */
  async uploadIndexSheet(workbookId: string, jobId: string, sheet: string, body: Uint8Array | string, encoding: 'gzip' | null): Promise<{ received: number; expected: number }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (encoding) headers['Content-Encoding'] = encoding;
    const path = `/agent-v2/workbooks/${encodeURIComponent(workbookId)}/index/jobs/${encodeURIComponent(jobId)}/sheets/${encodeURIComponent(sheet)}`;
    return this.json(await this.request(path, { method: 'PUT', headers, body: typeof body === 'string' ? body : new Blob([body as BlobPart]) }));
  }

  async commitIndexJob(workbookId: string, jobId: string): Promise<IndexStatus> {
    return this.json<IndexStatus>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/index/jobs/${encodeURIComponent(jobId)}/commit`, this.jsonInit('POST', {})));
  }

  /** POST a capture progress / failure report on the job (server log + job state). Never throws. */
  async reportIndexEvent(workbookId: string, jobId: string, body: { event: string; sheet?: string | null; ms?: number; cells?: number; bytes?: number; error?: string | null }): Promise<void> {
    try {
      await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/index/jobs/${encodeURIComponent(jobId)}/events`, this.jsonInit('POST', body));
    } catch {
      /* diagnostics only */
    }
  }

  async getIndexStatus(workbookId: string): Promise<IndexStatus> {
    return this.json<IndexStatus>(await this.request(`/agent-v2/workbooks/${encodeURIComponent(workbookId)}/index`));
  }

  /** POST /agent-v2/captures — one chunk of a sheet capture; chunk 0 answers with the `capture_id`. */
  async uploadCaptureChunk(body: CaptureChunkBody): Promise<CaptureUploadResponse> {
    return this.json<CaptureUploadResponse>(await this.request('/agent-v2/captures', this.jsonInit('POST', body)));
  }

  /** GET /agent-v2/audit-runs/{run_id}/report — the rows the add-in writes into the Audit sheet. */
  async getAuditReport(runId: string): Promise<AuditReport> {
    return this.json<AuditReport>(await this.request(`/agent-v2/audit-runs/${encodeURIComponent(runId)}/report`));
  }
}

export const apiService = new ApiService();
